import {
    PSelectWrapper,
    componentsReady,
    PSegmentedControl,
    PSegmentedControlItem,
    type SegmentedControlUpdateEvent,
} from '@porsche-design-system/components-react';
import { isBoolean, isNumber, isObject } from 'lodash/fp';
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { css } from 'styled-components';
import { type SelectProps } from '../context';
import { getTextFieldState } from './Fields/utils';

const SELECT_WRAPPER_TAG = 'p-select-wrapper';
const SELECT_WRAPPER_DROPDOWN_TAG = 'p-select-wrapper-dropdown';

const placeholderCss = css`
    :host .option:first-of-type,
    select option:first-of-type {
        display: none;
    }
`;

const useSelectStyle = (wrapperRef: React.RefObject<HTMLDivElement>, placeholder?: ReactNode) => {
    // note: hide first option if placeholder is set
    useEffect(() => {
        if (!wrapperRef.current || !placeholder) {
            return;
        }

        const wrapper = wrapperRef.current;

        componentsReady(wrapper).then(() => {
            const shadowRoot = wrapper.querySelector(SELECT_WRAPPER_TAG)?.shadowRoot;
            const dropdownShadowRoot = shadowRoot?.querySelector(SELECT_WRAPPER_DROPDOWN_TAG)?.shadowRoot;

            const style = document.createElement('style');
            style.textContent = placeholderCss.toString();

            dropdownShadowRoot?.appendChild(style);
        });
    }, [placeholder, wrapperRef]);
};

const stringify = (input: any): string => {
    if (isNumber(input)) {
        return input.toString();
    }

    if (isBoolean(input)) {
        return input ? 'true' : 'false';
    }

    if (Array.isArray(input) || isObject(input)) {
        return JSON.stringify(input);
    }

    return input;
};

// Keep the value in another properties
// Porsche v3 map the value as string internally
const useModifiedOptions = (original: SelectProps['options']) =>
    useMemo(() => {
        if (!original) {
            return [];
        }

        return original.map(option => ({ ...option, originalValue: option.value, value: stringify(option.value) }));
    }, [original]);

const useSegmentedStyle = (wrapperRef: React.RefObject<HTMLDivElement>) => {
    useEffect(() => {
        if (!wrapperRef.current) {
            return;
        }

        const wrapper = wrapperRef.current;

        componentsReady(wrapper).then(() => {
            const shadowRoot = wrapper.querySelector('p-segmented-control')?.shadowRoot;

            const sheet = new CSSStyleSheet();
            sheet.replaceSync(':host { gap: 16px !important; }');
            shadowRoot?.adoptedStyleSheets.push(sheet);
        });
    }, [wrapperRef]);
};

const SegmentedInner = ({ options, onChange, onSelect, disabled, value, brightness }: SelectProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    useSegmentedStyle(wrapperRef);

    const modifiedOptions = useModifiedOptions(options);
    const stringified = useMemo(() => stringify(value), [value]);

    const onUpdate = useCallback(
        (e: CustomEvent<SegmentedControlUpdateEvent>) => {
            const foundOption = modifiedOptions.find(option => option.value === e.detail.value);

            if (onChange) {
                onChange(foundOption ? foundOption?.originalValue : e.detail.value, { label: foundOption?.label });
            }

            if (onSelect) {
                onSelect(foundOption ? foundOption?.originalValue : e.detail.value, { label: foundOption?.label });
            }
        },
        [modifiedOptions, onChange, onSelect]
    );

    return (
        <div ref={wrapperRef}>
            <PSegmentedControl
                columns={modifiedOptions?.length ?? 3}
                onUpdate={onUpdate}
                theme={brightness === 'dark' ? 'dark' : undefined}
                value={stringified}
            >
                {modifiedOptions?.map(option => (
                    <PSegmentedControlItem key={option.value} disabled={disabled} value={option.value}>
                        {option.label}
                    </PSegmentedControlItem>
                ))}
            </PSegmentedControl>
        </div>
    );
};

const SelectInner = ({
    options,
    onChange,
    onSelect,
    placeholder,
    brightness,
    filterOption,
    disabled,
    value,
    status,
}: SelectProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    useSelectStyle(wrapperRef, placeholder);

    const modifiedOptions = useModifiedOptions(options);

    const renderedOptions = modifiedOptions?.map((option, index) => (
        <option key={option.value} selected={option.originalValue === value} value={option.value ?? 'null'}>
            {option.label}
        </option>
    ));

    const placeholderOption = placeholder && (
        <option style={{ display: 'none' }} value="" disabled selected>
            {placeholder}
        </option>
    );

    const onChangeHandler = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const foundOption = modifiedOptions.find(
                option => option.value === e.target.value || (e.target.value === 'null' && option.value === null)
            );

            if (onChange) {
                onChange(foundOption ? foundOption?.originalValue : e.target.value, { label: foundOption?.label });
            }

            if (onSelect) {
                onSelect(foundOption ? foundOption?.originalValue : e.target.value, { label: foundOption?.label });
            }
        },
        [onChange, onSelect, modifiedOptions]
    );

    return (
        <div ref={wrapperRef} className="porsche-v3-calculator-select-field-wrapper">
            <PSelectWrapper
                filter={!!filterOption}
                state={getTextFieldState(status)}
                theme={brightness === 'dark' ? 'dark' : undefined}
            >
                <select disabled={disabled} onChange={onChangeHandler} style={{ opacity: '1' }}>
                    {placeholderOption}
                    {renderedOptions}
                </select>
            </PSelectWrapper>
        </div>
    );
};

const Select = ({ segmented, ...others }: SelectProps) =>
    segmented ? <SegmentedInner {...others} /> : <SelectInner {...others} />;

export default Select;
